import React, { useEffect, useState } from 'react';
import { uniqBy } from 'lodash';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import axiosInstant from '../../api';
import { useUser } from '../../hooks/useUser';
import { useGetBrandTeam } from './hooks/useGetBrandTeam';
import { MainContainer } from '../../components/ContentWrapper';
import BrandDetailMenu from './BrandDetailMenu';
import { IBrand } from './team/data/type';
import { IProduct } from './components/ProductList';
import { ISinglePro } from '../../components/singlePro/state';
import { isInternal } from '../../utils';
import { DotLottleLoader } from '../../components/Loader/DotLottleLoader';
import { BrandTeamMemberData } from './settings/team/data/type';

export const PRODUCTS_PER_PAGE = 20;
const EXPERTS_PER_PAGE = 100;
export interface IBrandDetailsContext {
  brand: IBrand;
  setBrand: React.Dispatch<React.SetStateAction<IBrand>>;
  brandProducts: IProduct[];
  brandProductsCount: number;
  brandExperts: ISinglePro[];
  brandTeam: BrandTeamMemberData[];
  getBrandTeam: () => void;
  getBrandExperts: () => Promise<void>;
  getBrandProducts: (page?: number) => Promise<void>;
}

export function SingleBrandDetails() {
  const { role } = useUser();
  const { id } = useParams();
  const brandId = id ? parseInt(id, 10) : 0;

  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState<IBrand>({ name: '' });
  const [brandProducts, setBrandProducts] = useState<IProduct[]>([]);
  const [brandProductsCount, setBrandProductsCount] = useState(0);
  const [brandExperts, setBrandExperts] = useState<ISinglePro[]>([]);
  const { brandTeam, getBrandTeam } = useGetBrandTeam(brandId);

  // prevent member to direct access to this page
  const navigate = useNavigate();
  if (!isInternal(role)) navigate('/');

  const getBrandInfo = async () => {
    const response = await axiosInstant.get(`/brands/${brandId}`);
    if (response?.data) setBrand(response.data);
  };

  const fetchBrandExpertsPage = (page: number) =>
    axiosInstant.get(`/brands/${brandId}/pros`, {
      params: { page, limit: EXPERTS_PER_PAGE },
    });

  const getBrandExperts = async () => {
    const first = await fetchBrandExpertsPage(1);
    const firstRows: ISinglePro[] = first?.data?.rows || [];
    const total = first?.data?.count || 0;
    const remaining = Math.max(Math.ceil(total / EXPERTS_PER_PAGE) - 1, 0);
    const rest = await Promise.all(
      Array.from({ length: remaining }, (_, i) => fetchBrandExpertsPage(i + 2))
    );
    // Pages are fetched separately, so dedupe by userid in case the set
    // shifted between requests.
    setBrandExperts(
      uniqBy(
        rest.reduce<ISinglePro[]>(
          (all, response) => all.concat(response?.data?.rows || []),
          firstRows
        ),
        'userid'
      )
    );
  };

  const getBrandProducts = async (page = 1) => {
    const response = await axiosInstant.get(`/brands/${brandId}/desires`, {
      params: { page, limit: PRODUCTS_PER_PAGE },
    });
    const uniqueProducts =
      page === 1
        ? response.data.rows
        : uniqBy([...brandProducts, ...response.data.rows], 'desireid');
    if (response?.data) {
      setBrandProducts(uniqueProducts || []);
      setBrandProductsCount(response.data.count);
    }
  };

  const loadData = async () => {
    setLoading(true);
    Promise.allSettled([
      getBrandInfo(),
      getBrandTeam(),
      getBrandProducts(),
      getBrandExperts(),
    ]).then(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <MainContainer>
      <div className="flex justify-between items-start">
        <BrandDetailMenu id={id} brandData={brand} />
        <Box className="w-full" sx={{ marginLeft: 4 }}>
          {loading ? (
            <DotLottleLoader />
          ) : (
            <Outlet
              context={{
                brand,
                setBrand,
                brandProducts,
                brandProductsCount,
                brandExperts,
                brandTeam,
                getBrandTeam,
                getBrandExperts,
                getBrandProducts,
              }}
            />
          )}
        </Box>
      </div>
    </MainContainer>
  );
}
